import { useDraggable, useDroppable } from '@dnd-kit/core';
import cn from 'classnames';
import type { MouseEvent } from 'react';
import { useState } from 'react';

import { Remove } from '@webapp/common/assets/icons';
import { EditName } from '@webapp/common/components/edit-name';
import { ImageWithGallery } from '@webapp/common/components/image-with-gallery';
import { setHtml } from '@webapp/common/lib/ui';
import { initialSurveyAddonAlert } from '@webapp/common/resources/entities/survey-addons/alert';

import { selectInfo } from '@webapp/survey/src/machine/selectors';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import css from './card-sorting.css';

interface ICard {
    id: number;
    text: string;
    file?: string;
}

export const Card: FC<{ file?: string; text: string; isDragging?: boolean }> = ({ file, text }) => {
    return (
        <div className={css.card}>
            <ImageWithGallery imageUrl={file} alt='card-image' />
            <div>{text}</div>
        </div>
    );
};

export const Draggable: FC<{ id: number; file?: string; text: string }> = ({ id, file, text }) => {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id,
    });

    return (
        <div
            className={cn(css.draggable, { [css.draggableActive]: isDragging })}
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            onMouseDown={(e: MouseEvent) => {
                const target = e.target as unknown as { dataset: { noDnd: boolean } };

                if (target?.dataset?.noDnd) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }
                listeners.onMouseDown(e);
            }}
        >
            <Card text={text} file={file} />
        </div>
    );
};

export const Cards: FC<{ cards: Array<ICard>; sortedCards?: Record<string, string>; isFullHeight?: boolean }> = ({
    cards,
    sortedCards,
    isFullHeight,
}) => {
    const { setNodeRef, isOver, active } = useDroppable({
        id: 'cards',
    });
    const {
        params: {
            alert: { cardSortedText, cardSortingNoCardsText },
        },
    } = useStateSelector(selectInfo);
    const unsortedCards = cards.filter((card) => !sortedCards?.[card?.id]);
    const isActive = Boolean(active) && !unsortedCards?.find((item) => item.id === active.id);

    const renderCards = () => {
        if (!cards.length) {
            return cardSortingNoCardsText;
        } else if (!unsortedCards.length) {
            return cardSortedText;
        }

        return unsortedCards.map(({ id, file, text }) => <Draggable id={id} text={text} file={file} key={id} />);
    };

    return (
        <div
            ref={setNodeRef}
            className={cn(css.cards, { [css.cardsOver]: isOver && isActive, [css.cardsFullHeight]: isFullHeight })}
        >
            {renderCards()}
        </div>
    );
};

export const Category: FC<{ id: number; name: string; cards?: Array<ICard>; descriptionText: string }> = ({
    id,
    name,
    cards,
    descriptionText,
}) => {
    const { setNodeRef, isOver, active } = useDroppable({ id });
    const isActive = Boolean(active) && !cards?.find((item) => item.id === active.id);

    return (
        <div ref={setNodeRef} className={cn(css.category, { [css.categoryOver]: isOver && isActive })}>
            <div className={css.categoryName} {...setHtml(name)} />
            {!cards?.length && <div className={cn(css.categoryOverContent)}>{descriptionText}</div>}
            {cards?.map(({ id, text, file }) => (
                <Draggable key={id} id={id} text={text} file={file} />
            ))}
        </div>
    );
};

export const CreatedCategory: FC<{
    id: string;
    name: string;
    cards?: Array<ICard>;
    onChange: (id: string, newName: string) => void;
    onRemove: (id: string) => void;
    descriptionText: string;
    cardSortingCategoryNameText: string;
}> = ({ id, name, cards, onChange, onRemove, descriptionText, cardSortingCategoryNameText }) => {
    const { setNodeRef, isOver, active } = useDroppable({ id });
    const isActive = Boolean(active) && !cards?.find((item) => item.id === active.id);
    const [showEditName, setShowEditName] = useState(false);

    return (
        <div ref={setNodeRef} className={cn(css.category, { [css.categoryOver]: isOver && isActive })}>
            <div className={css.nameWithButtons}>
                {showEditName ? (
                    <EditName
                        name={name}
                        fullWidth={true}
                        className={css.editName}
                        onDone={() => setShowEditName(false)}
                        onSave={(name: string) => onChange(id, name)}
                    />
                ) : (
                    <div
                        className={cn(css.newCategoryName, { [css.newCategoryNameError]: !name.length })}
                        onClick={() => setShowEditName(true)}
                    >
                        {name || cardSortingCategoryNameText || initialSurveyAddonAlert.cardSortingCategoryNameText}
                    </div>
                )}
                <div className={css.nameWithButtonsIcons}>
                    <Remove onClick={() => onRemove(id)} />
                </div>
            </div>

            {!cards?.length && <div className={cn(css.categoryOverContent)}>{descriptionText}</div>}
            {cards?.map(({ id, text, file }) => (
                <Draggable key={id} id={id} text={text} file={file} />
            ))}
        </div>
    );
};

export const NewCategory: FC<{ descriptionText: string }> = ({ descriptionText }) => {
    const { setNodeRef, isOver } = useDroppable({ id: 'newCategory' });

    return (
        <div ref={setNodeRef} className={cn(css.newCategory, { [css.newCategoryOver]: isOver })}>
            {descriptionText}
        </div>
    );
};
